import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CmsFormPanel, PanelContent, PanelHeader, PanelTitle } from '@/components/shared/CmsFormPanel';
import { ConfirmDialog } from '@/components/shared';
import {
  BIB_NUMBERING_SCOPE_RACE_TYPE,
  BIB_NUMBERING_SCOPE_SHARED_RACE_POOL,
} from '@/utils/bibNumbering';
import type { RaceType, RaceTypeFormData } from './racetypesmanager.props';

interface RaceTypesFormDialogProps {
  isDialogOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
  editingRaceType: RaceType | null;
  formData: RaceTypeFormData;
  setFormData: (data: RaceTypeFormData) => void;
  handleSubmit: (e: React.FormEvent) => void;
  deleteTarget: any;
  setDeleteTarget: (target: any) => void;
  confirmDelete: () => void;
}

const helperClassName = 'mt-1 text-xs text-muted-foreground';

const RaceTypesFormDialog: React.FC<RaceTypesFormDialogProps> = ({
  isDialogOpen, setIsDialogOpen, editingRaceType, formData, setFormData,
  handleSubmit, deleteTarget, setDeleteTarget, confirmDelete,
}) => {
  const numberingEnabledValue = formData.bibNumberingEnabled ? 'enabled' : 'disabled';
  const usesSharedPool = formData.bibNumberingEnabled && formData.bibNumberingScope === BIB_NUMBERING_SCOPE_SHARED_RACE_POOL;

  return (
    <>
      <CmsFormPanel open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <PanelContent>
          <PanelHeader>
            <PanelTitle>{editingRaceType ? 'Измени тип на трка' : 'Додај нов тип на трка'}</PanelTitle>
          </PanelHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Име *</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="distance">Дистанца (км) *</Label>
                <Input id="distance" type="number" step="0.001" value={formData.distance} onChange={(e) => setFormData({ ...formData, distance: e.target.value })} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Цена (МКД) *</Label>
                <Input id="price" type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="packageType">Тип на пакет *</Label>
                <select id="packageType" value={formData.packageType} onChange={(e) => setFormData({ ...formData, packageType: e.target.value })} className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring" required>
                  <option value="Basic">Basic</option>
                  <option value="Standard">Standard</option>
                  <option value="Premium">Premium</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bibNumberingEnabled">Доделување стартни броеви *</Label>
                <select
                  id="bibNumberingEnabled"
                  value={numberingEnabledValue}
                  onChange={(e) => setFormData({
                    ...formData,
                    bibNumberingEnabled: e.target.value === 'enabled',
                    bibNumberingScope: e.target.value === 'enabled' ? formData.bibNumberingScope : BIB_NUMBERING_SCOPE_RACE_TYPE,
                    bibNumberingPoolKey: e.target.value === 'enabled' ? formData.bibNumberingPoolKey : '',
                  })}
                  className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                >
                  <option value="enabled">Вклучено</option>
                  <option value="disabled">Исклучено</option>
                </select>
                <p className={helperClassName}>Кога е исклучено, регистрациите за овој тип можат да останат без стартен број.</p>
              </div>
              <div>
                <Label htmlFor="bibNumberingScope">Опсег на нумерација *</Label>
                <select
                  id="bibNumberingScope"
                  value={formData.bibNumberingScope}
                  onChange={(e) => setFormData({
                    ...formData,
                    bibNumberingScope: e.target.value,
                    bibNumberingPoolKey: e.target.value === BIB_NUMBERING_SCOPE_SHARED_RACE_POOL ? formData.bibNumberingPoolKey : '',
                  })}
                  className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
                  disabled={!formData.bibNumberingEnabled}
                  required
                >
                  <option value={BIB_NUMBERING_SCOPE_RACE_TYPE}>Одделен за овој тип</option>
                  <option value={BIB_NUMBERING_SCOPE_SHARED_RACE_POOL}>Заеднички pool во настанот</option>
                </select>
                <p className={helperClassName}>Одделен опсег е единствен само во типот; shared pool дели исти броеви со други типови од истиот настан.</p>
              </div>
            </div>
            <div>
              <Label htmlFor="bibNumberingPoolKey">Pool key</Label>
              <Input
                id="bibNumberingPoolKey"
                value={formData.bibNumberingPoolKey}
                onChange={(e) => setFormData({ ...formData, bibNumberingPoolKey: e.target.value })}
                placeholder="Напр. 10K-OPEN"
                disabled={!usesSharedPool}
                required={usesSharedPool}
              />
              <p className={helperClassName}>Потребно само за shared pool. Користете ист key кај сите типови што треба да делат исти стартни броеви.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startNumber">Почетен број *</Label>
                <Input id="startNumber" type="number" value={formData.startNumber} onChange={(e) => setFormData({ ...formData, startNumber: e.target.value })} required />
                <p className={helperClassName}>Почеток на дозволениот опсег. Ако користите shared pool, опсезите од сите поврзани типови се комбинираат.</p>
              </div>
              <div>
                <Label htmlFor="endNumber">Краен број *</Label>
                <Input id="endNumber" type="number" value={formData.endNumber} onChange={(e) => setFormData({ ...formData, endNumber: e.target.value })} required />
                <p className={helperClassName}>Крај на дозволениот опсег за автоматско и рачно доделување броеви.</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="maxBibNumber">Макс број на стартни бројки *</Label>
                <Input id="maxBibNumber" type="number" value={formData.maxBibNumber} onChange={(e) => setFormData({ ...formData, maxBibNumber: e.target.value })} required />
                <p className={helperClassName}>Задржано за backward compatibility со постојните конфигурации.</p>
              </div>
              <div>
                <Label htmlFor="maxParticipants">Макс учесници *</Label>
                <Input id="maxParticipants" type="number" value={formData.maxParticipants} onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="numberOfZones">Број на зони *</Label>
                <Input id="numberOfZones" type="number" value={formData.numberOfZones} onChange={(e) => setFormData({ ...formData, numberOfZones: Number(e.target.value) })} required />
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Откажи</Button>
              <Button type="submit">{editingRaceType ? 'Зачувај промени' : 'Креирај тип на трка'}</Button>
            </div>
          </form>
        </PanelContent>
      </CmsFormPanel>
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Избриши тип на трка"
        description="Дали сте сигурни дека сакате да го избришете овој тип на трка?"
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default RaceTypesFormDialog;
